/**
 * useCacheManagement Hook
 *
 * Manages browser session storage for gallery state persistence including:
 * - State save/load operations with debouncing
 * - Cache key management and versioning
 * - Error handling and fallback strategies
 * - Cache invalidation and cleanup
 * - Timeout management for save operations
 */

import { useCallback, useRef } from 'react'

const CACHE_VERSION = '1.0'
const DEFAULT_CACHE_KEY = 'gallery_state'
const SAVE_DEBOUNCE_DELAY = 500

export function useCacheManagement(cacheKey = DEFAULT_CACHE_KEY) {
  const saveTimeoutRef = useRef(null)

  /**
   * Generate a versioned cache key to handle schema changes
   */
  const getVersionedCacheKey = useCallback(key => {
    return `${key}_v${CACHE_VERSION}`
  }, [])

  /**
   * Remove old/stale cache entries to free up space
   */
  const invalidateOldCache = useCallback(() => {
    try {
      const keysToCheck = []
      const maxAge = 24 * 60 * 60 * 1000 // 24 hours

      // Collect all cache keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.includes('gallery_state')) {
          keysToCheck.push(key)
        }
      }

      // Check each key and remove stale entries
      keysToCheck.forEach(key => {
        try {
          const cachedData = sessionStorage.getItem(key)
          if (cachedData) {
            const parsedData = JSON.parse(cachedData)
            const cacheAge = Date.now() - (parsedData.timestamp || 0)

            if (cacheAge > maxAge) {
              sessionStorage.removeItem(key)
            }
          }
        } catch (parseError) {
          // If we can't parse it, remove it
          sessionStorage.removeItem(key)
        }
      })

      return true
    } catch (error) {
      console.error('Failed to clean old cache entries:', error)
      return false
    }
  }, [])

  /**
   * Save state to session storage with debouncing and error handling
   */
  const saveStateToCache = useCallback(
    (state, customKey = null) => {
      // Clear existing timeout to implement debouncing
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(() => {
        try {
          const key = getVersionedCacheKey(customKey || cacheKey)

          // Create minimal state to avoid storing unnecessary data
          const minimalState = {
            navigation: state.navigation || [],
            selectedCategories: state.selectedCategories || [],
            showAssets: state.showAssets || false,
            timestamp: Date.now(),
            version: CACHE_VERSION
          }

          const serializedState = JSON.stringify(minimalState)

          // Check if we're exceeding storage limits
          if (serializedState.length > 1024 * 1024) {
            // 1MB limit
            console.warn('Cache state is too large, skipping save operation')
            return false
          }

          sessionStorage.setItem(key, serializedState)
          return true
        } catch (error) {
          console.error('Failed to save state to cache:', error)

          // If storage is full, try to clear old cache entries
          if (error.name === 'QuotaExceededError') {
            invalidateOldCache()
            // Retry once after cleanup
            try {
              const key = getVersionedCacheKey(customKey || cacheKey)
              const minimalState = {
                navigation: state.navigation || [],
                selectedCategories: state.selectedCategories || [],
                showAssets: state.showAssets || false,
                timestamp: Date.now(),
                version: CACHE_VERSION
              }
              sessionStorage.setItem(key, JSON.stringify(minimalState))
              return true
            } catch (retryError) {
              console.error(
                'Failed to save state after cache cleanup:',
                retryError
              )
              return false
            }
          }
          return false
        }
      }, SAVE_DEBOUNCE_DELAY)
    },
    [cacheKey, getVersionedCacheKey, invalidateOldCache]
  )

  /**
   * Clear specific cache entry
   */
  const invalidateCache = useCallback(
    (customKey = null) => {
      try {
        const key = getVersionedCacheKey(customKey || cacheKey)
        sessionStorage.removeItem(key)
        return true
      } catch (error) {
        console.error('Failed to invalidate cache:', error)
        return false
      }
    },
    [cacheKey, getVersionedCacheKey]
  )

  /**
   * Load state from session storage with error handling and validation
   */
  const loadStateFromCache = useCallback(
    (customKey = null) => {
      try {
        const key = getVersionedCacheKey(customKey || cacheKey)
        const cachedState = sessionStorage.getItem(key)

        if (!cachedState) {
          return null
        }

        const parsedState = JSON.parse(cachedState)

        // Validate cache version
        if (parsedState.version !== CACHE_VERSION) {
          console.warn('Cache version mismatch, invalidating cached state')
          invalidateCache(customKey)
          return null
        }

        // Check if cache is stale (older than 24 hours)
        const cacheAge = Date.now() - (parsedState.timestamp || 0)
        const maxAge = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

        if (cacheAge > maxAge) {
          console.warn('Cache is stale, invalidating cached state')
          invalidateCache(customKey)
          return null
        }

        // Remove metadata before returning
        const { timestamp, version, ...stateData } = parsedState
        return stateData
      } catch (error) {
        console.error('Failed to load state from cache:', error)

        // If parsing fails, clear the corrupted cache
        invalidateCache(customKey)
        return null
      }
    },
    [cacheKey, getVersionedCacheKey, invalidateCache]
  )

  /**
   * Clear all gallery-related cache entries
   */
  const invalidateAllCache = useCallback(() => {
    try {
      const keysToRemove = []

      // Find all gallery-related keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.includes('gallery_state')) {
          keysToRemove.push(key)
        }
      }

      // Remove all found keys
      keysToRemove.forEach(key => sessionStorage.removeItem(key))

      return true
    } catch (error) {
      console.error('Failed to invalidate all cache:', error)
      return false
    }
  }, [])

  /**
   * Get cache information and statistics
   */
  const getCacheInfo = useCallback(
    (customKey = null) => {
      try {
        const key = getVersionedCacheKey(customKey || cacheKey)
        const cachedState = sessionStorage.getItem(key)

        if (!cachedState) {
          return {
            exists: false,
            size: 0,
            age: 0,
            version: null
          }
        }

        const parsedState = JSON.parse(cachedState)
        const cacheAge = Date.now() - (parsedState.timestamp || 0)

        return {
          exists: true,
          size: cachedState.length,
          age: cacheAge,
          version: parsedState.version,
          isStale: cacheAge > 24 * 60 * 60 * 1000
        }
      } catch (error) {
        console.error('Failed to get cache info:', error)
        return {
          exists: false,
          size: 0,
          age: 0,
          version: null,
          error: error.message
        }
      }
    },
    [cacheKey, getVersionedCacheKey]
  )

  /**
   * Cleanup function to clear any pending timeouts
   */
  const cleanup = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
  }, [])

  return {
    // Core cache operations
    saveStateToCache,
    loadStateFromCache,

    // Cache invalidation
    invalidateCache,
    invalidateAllCache,
    invalidateOldCache,

    // Utilities
    getCacheInfo,
    cleanup,

    // Configuration
    cacheKey: getVersionedCacheKey(cacheKey)
  }
}

export default useCacheManagement
